/**
 * Common medicine catalog for Tanzania chemists (stock entry helper).
 * Includes short clinical hints — NOT prescribing advice; verify with STG / qualified staff.
 */

/** @typedef {{ id: string, label: string }} PharmacyTherapeuticGroup */
/**
 * @typedef {Object} PharmacyCommonDrug
 * @property {string} id
 * @property {string} name
 * @property {string} group
 * @property {string} category
 * @property {string} unit
 * @property {string} ingredient — active substance (INN)
 * @property {string} uses — what it is commonly used for
 * @property {string} route — how it is taken / applied
 * @property {string} [caution] — brief safety note
 */

export const PHARMACY_DRUG_INFO_DISCLAIMER =
  'Guidance only — not a substitute for a clinician, STG, or a prescription. Confirm dose and use before dispensing.';

export const PHARMACY_THERAPEUTIC_GROUPS = [
  { id: 'antibiotics', label: 'Antibiotics' },
  { id: 'antimalarials', label: 'Antimalarials' },
  { id: 'analgesics', label: 'Analgesics & antipyretics' },
  { id: 'antihypertensives', label: 'Antihypertensives' },
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'respiratory', label: 'Respiratory & allergy' },
  { id: 'gastro', label: 'Stomach & diarrhoea' },
  { id: 'vitamins', label: 'Vitamins & supplements' },
  { id: 'family_planning', label: 'Family planning' },
  { id: 'topical', label: 'Creams & topical' },
  { id: 'paediatric', label: 'Paediatric syrups' },
  { id: 'injections', label: 'Injections & vaccines' },
  { id: 'devices', label: 'Devices & first aid' },
];

/** @param {Omit<PharmacyCommonDrug, 'ingredient'|'uses'|'route'> & Partial<Pick<PharmacyCommonDrug, 'ingredient'|'uses'|'route'|'caution'>>} d */
function rx(d) {
  return {
    ingredient: d.ingredient || d.name.split(/\d/)[0].trim(),
    uses: d.uses || '',
    route: d.route || 'Oral',
    ...d,
  };
}

/** @type {PharmacyCommonDrug[]} */
export const PHARMACY_COMMON_DRUGS = [
  rx({
    id: 'amox-500',
    name: 'Amoxicillin 500mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Amoxicillin',
    uses: 'Bacterial infections: pneumonia, throat/sinus, skin (as directed).',
    route: 'Oral — capsules',
    caution: 'Prescription. Penicillin allergy — do not supply. Complete the full course.',
  }),
  rx({
    id: 'amox-250',
    name: 'Amoxicillin 250mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Amoxicillin',
    uses: 'Bacterial infections (lower dose / older children per STG).',
    route: 'Oral',
    caution: 'Prescription. Check for penicillin allergy.',
  }),
  rx({
    id: 'amox-susp',
    name: 'Amoxicillin 125mg/5ml suspension',
    group: 'antibiotics',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Amoxicillin',
    uses: 'Bacterial infections in children (pneumonia, ear, etc. by age).',
    route: 'Oral — suspension',
    caution: 'Prescription. Dose by weight/age — follow STG.',
  }),
  rx({
    id: 'ampicillin',
    name: 'Ampicillin 500mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Ampicillin',
    uses: 'Bacterial infections (e.g. typhoid, some urinary tract infections).',
    route: 'Oral',
    caution: 'Prescription. Penicillin allergy — high risk.',
  }),
  rx({
    id: 'azithro-500',
    name: 'Azithromycin 500mg tablets',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Azithromycin',
    uses: 'Bacterial infections: trachoma, pneumonia, some STIs as directed.',
    route: 'Oral',
    caution: 'Prescription. Short course — follow instructions completely.',
  }),
  rx({
    id: 'cipro-500',
    name: 'Ciprofloxacin 500mg tablets',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Ciprofloxacin',
    uses: 'Infections needing a quinolone: typhoid, dysentery, severe cases as directed.',
    route: 'Oral',
    caution: 'Prescription. Avoid in young children unless directed. May cause tendon pain.',
  }),
  rx({
    id: 'cefixime',
    name: 'Cefixime 400mg tablets',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Cefixime',
    uses: 'Bacterial infections (e.g. typhoid, gonorrhoea per guidelines).',
    route: 'Oral',
    caution: 'Prescription. Cephalosporin — check allergy history.',
  }),
  rx({
    id: 'doxycycline',
    name: 'Doxycycline 100mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Doxycycline',
    uses: 'Infections, acne, malaria prophylaxis (per STG).',
    route: 'Oral — with food',
    caution: 'Prescription. Avoid in pregnancy; young children — see STG.',
  }),
  rx({
    id: 'metronidazole-400',
    name: 'Metronidazole 400mg tablets',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Metronidazole',
    uses: 'Protozoal and anaerobic bacterial infections: amoebiasis, giardiasis, trichomoniasis, dental infections.',
    route: 'Oral',
    caution: 'Prescription. Do not drink alcohol during treatment.',
  }),
  rx({
    id: 'metronidazole-susp',
    name: 'Metronidazole 200mg/5ml suspension',
    group: 'antibiotics',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Metronidazole',
    uses: 'GI / protozoal infections in children (weight-based dose).',
    route: 'Oral',
    caution: 'Prescription. Dose by age/weight.',
  }),
  rx({
    id: 'cotrim',
    name: 'Co-trimoxazole 480mg tablets',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Sulfamethoxazole + trimethoprim',
    uses: 'Bacterial infections: UTI, pneumonia, prophylaxis (e.g. PCP) per STG.',
    route: 'Oral',
    caution: 'Prescription. Encourage fluids; check sulfa allergy.',
  }),
  rx({
    id: 'cloxacillin',
    name: 'Cloxacillin 500mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Cloxacillin',
    uses: 'Skin infections / boils resistant to plain penicillin.',
    route: 'Oral — before meals',
    caution: 'Prescription.',
  }),
  rx({
    id: 'erythromycin',
    name: 'Erythromycin 250mg tablets',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Erythromycin',
    uses: 'When penicillin is not suitable; some skin / throat infections.',
    route: 'Oral',
    caution: 'Prescription.',
  }),
  rx({
    id: 'clindamycin',
    name: 'Clindamycin 300mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Clindamycin',
    uses: 'Dental, skin, and anaerobic infections (as directed).',
    route: 'Oral',
    caution: 'Prescription.',
  }),
  rx({
    id: 'fluclox',
    name: 'Flucloxacillin 500mg capsules',
    group: 'antibiotics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Flucloxacillin',
    uses: 'Boils, cellulitis, skin infections.',
    route: 'Oral — before meals',
    caution: 'Prescription.',
  }),

  rx({
    id: 'alu',
    name: 'Artemether–Lumefantrine (ALU) 20/120mg',
    group: 'antimalarials',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Artemether + lumefantrine',
    uses: 'Uncomplicated malaria — first-line ACT in Tanzania.',
    route: 'Oral — with fatty food',
    caution: 'Prescription. Complete full course. Avoid early pregnancy.',
  }),
  rx({
    id: 'aa',
    name: 'Artesunate + Amodiaquine (AA)',
    group: 'antimalarials',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Artesunate + amodiaquine',
    uses: 'Uncomplicated malaria (alternative ACT).',
    route: 'Oral',
    caution: 'Prescription. Complete full course.',
  }),
  rx({
    id: 'quinine',
    name: 'Quinine 300mg tablets',
    group: 'antimalarials',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Quinine',
    uses: 'Severe malaria (hospital) or specialist-directed use.',
    route: 'Oral',
    caution: 'Prescription. Not for routine uncomplicated malaria — use ACT.',
  }),
  rx({
    id: 'sulfadox-pyrim',
    name: 'Sulfadoxine–Pyrimethamine (SP)',
    group: 'antimalarials',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Sulfadoxine + pyrimethamine',
    uses: 'Intermittent preventive treatment (IPTp) in pregnancy — per national program.',
    route: 'Oral — single supervised dose',
    caution: 'Prescription. Avoid if sulfa allergy.',
  }),

  rx({
    id: 'para-500',
    name: 'Paracetamol 500mg tablets',
    group: 'analgesics',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Paracetamol',
    uses: 'Pain, fever, headache, common cold symptoms.',
    route: 'Oral',
    caution: 'Do not exceed max dose — liver risk. Show paediatric dosing.',
  }),
  rx({
    id: 'para-syrup',
    name: 'Paracetamol 120mg/5ml syrup',
    group: 'analgesics',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Paracetamol',
    uses: 'Fever and pain in children.',
    route: 'Oral',
    caution: 'Dose by weight — avoid duplicate paracetamol products.',
  }),
  rx({
    id: 'ibuprofen-400',
    name: 'Ibuprofen 400mg tablets',
    group: 'analgesics',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Ibuprofen',
    uses: 'Pain, inflammation, fever, muscle pain.',
    route: 'Oral — after food',
    caution: 'Avoid with stomach ulcer, pregnancy, or kidney problems.',
  }),
  rx({
    id: 'diclofenac',
    name: 'Diclofenac 50mg tablets',
    group: 'analgesics',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Diclofenac',
    uses: 'Pain and inflammation (arthritis, injury, menstrual pain).',
    route: 'Oral',
    caution: 'NSAID — heart/kidney risk with long-term use.',
  }),
  rx({
    id: 'aspirin',
    name: 'Aspirin 300mg tablets',
    group: 'analgesics',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Acetylsalicylic acid',
    uses: 'Pain, fever; low-dose forms for heart protection (different SKU).',
    route: 'Oral',
    caution: 'Do not give to children with fever (Reye syndrome risk).',
  }),
  rx({
    id: 'tramadol',
    name: 'Tramadol 50mg capsules',
    group: 'analgesics',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Tramadol',
    uses: 'Moderate–severe pain (controlled — prescription only).',
    route: 'Oral',
    caution: 'Prescription. May cause drowsiness; misuse/dependence risk.',
  }),

  rx({
    id: 'amlodipine',
    name: 'Amlodipine 5mg tablets',
    group: 'antihypertensives',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Amlodipine',
    uses: 'High blood pressure (hypertension), angina.',
    route: 'Oral',
    caution: 'Prescription. Long-term use — follow clinician advice.',
  }),
  rx({
    id: 'losartan',
    name: 'Losartan 50mg tablets',
    group: 'antihypertensives',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Losartan',
    uses: 'High blood pressure, kidney protection in diabetes.',
    route: 'Oral',
    caution: 'Prescription. Avoid in pregnancy.',
  }),
  rx({
    id: 'hctz',
    name: 'Hydrochlorothiazide 25mg tablets',
    group: 'antihypertensives',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Hydrochlorothiazide',
    uses: 'High blood pressure, fluid retention (diuretic).',
    route: 'Oral',
    caution: 'Prescription. May lower potassium levels.',
  }),
  rx({
    id: 'nifedipine',
    name: 'Nifedipine 20mg tablets',
    group: 'antihypertensives',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Nifedipine',
    uses: 'High blood pressure, pre-eclampsia when directed.',
    route: 'Oral',
    caution: 'Prescription.',
  }),
  rx({
    id: 'atenolol',
    name: 'Atenolol 50mg tablets',
    group: 'antihypertensives',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Atenolol',
    uses: 'High blood pressure, heart rate control.',
    route: 'Oral',
    caution: 'Prescription. Do not stop abruptly.',
  }),

  rx({
    id: 'metformin',
    name: 'Metformin 500mg tablets',
    group: 'diabetes',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Metformin',
    uses: 'Type 2 diabetes — blood sugar control.',
    route: 'Oral — with or after meals',
    caution: 'Prescription. Stop before contrast/surgery (ask clinician).',
  }),
  rx({
    id: 'glibenclamide',
    name: 'Glibenclamide 5mg tablets',
    group: 'diabetes',
    category: 'Tablets & capsules (Rx)',
    unit: 'strip',
    ingredient: 'Glibenclamide',
    uses: 'Type 2 diabetes — stimulates insulin release.',
    route: 'Oral',
    caution: 'Prescription. Hypoglycaemia risk — counsel on symptoms.',
  }),
  rx({
    id: 'insulin',
    name: 'Insulin (vials — specify type)',
    group: 'diabetes',
    category: 'Injections & vaccines',
    unit: 'vial',
    ingredient: 'Insulin',
    uses: 'Type 1 & 2 diabetes when tablets alone are not enough.',
    route: 'Subcutaneous injection',
    caution: 'Prescription. Keep refrigerated; record type (NPH, rapid, etc.).',
  }),

  rx({
    id: 'salbutamol-inh',
    name: 'Salbutamol inhaler 100mcg',
    group: 'respiratory',
    category: 'Inhalers & nebuliser supplies',
    unit: 'pcs',
    ingredient: 'Salbutamol',
    uses: 'Asthma, bronchiolitis — relieves wheeze (short-acting bronchodilator).',
    route: 'Inhaled (inhaler)',
    caution: 'Teach correct technique. Prescription for regular use.',
  }),
  rx({
    id: 'cetirizine',
    name: 'Cetirizine 10mg tablets',
    group: 'respiratory',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Cetirizine',
    uses: 'Allergy, runny nose, itchy eyes.',
    route: 'Oral',
    caution: 'May cause mild drowsiness.',
  }),
  rx({
    id: 'loratadine',
    name: 'Loratadine 10mg tablets',
    group: 'respiratory',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Loratadine',
    uses: 'Allergy, hay fever.',
    route: 'Oral',
  }),
  rx({
    id: 'chlorpheniramine',
    name: 'Chlorpheniramine 4mg tablets',
    group: 'respiratory',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Chlorpheniramine',
    uses: 'Allergy, hay fever, cough.',
    route: 'Oral',
    caution: 'Causes drowsiness — avoid driving.',
  }),
  rx({
    id: 'cough-syrup',
    name: 'Cough syrup (compound — specify brand)',
    group: 'respiratory',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Mixed (read label)',
    uses: 'Dry or productive cough — choose the right formula.',
    route: 'Oral',
    caution: 'Read ingredients — avoid codeine in children.',
  }),

  rx({
    id: 'omeprazole',
    name: 'Omeprazole 20mg capsules',
    group: 'gastro',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Omeprazole',
    uses: 'Ulcer, acid reflux, acid-related stomach pain.',
    route: 'Oral — before meals',
  }),
  rx({
    id: 'antacid',
    name: 'Antacid suspension',
    group: 'gastro',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Aluminium/Magnesium salts',
    uses: 'Stomach pain, excess acid, ulcer symptoms (symptomatic relief).',
    route: 'Oral',
  }),
  rx({
    id: 'ors',
    name: 'Oral rehydration salts (ORS)',
    group: 'gastro',
    category: 'Syrups & suspensions',
    unit: 'sachet',
    ingredient: 'Glucose + electrolytes',
    uses: 'Diarrhoea, vomiting, dehydration.',
    route: 'Oral — mixed in safe water',
    caution: 'Mix to correct strength; continue feeding.',
  }),
  rx({
    id: 'zinc-syrup',
    name: 'Zinc sulfate 20mg/5ml syrup',
    group: 'gastro',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Zinc',
    uses: 'Diarrhoea in children (shortens duration and severity).',
    route: 'Oral',
    caution: 'Dose by age — follow STG.',
  }),
  rx({
    id: 'loperamide',
    name: 'Loperamide 2mg capsules',
    group: 'gastro',
    category: 'Tablets & capsules (OTC)',
    unit: 'strip',
    ingredient: 'Loperamide',
    uses: 'Acute diarrhoea (not for young children with fever).',
    route: 'Oral',
    caution: 'Do not use for bloody diarrhoea or fever in children.',
  }),

  rx({
    id: 'fefol',
    name: 'Ferrous sulfate + folic acid (Fefol)',
    group: 'vitamins',
    category: 'Vitamins & supplements',
    unit: 'strip',
    ingredient: 'Iron + folic acid',
    uses: 'Iron-deficiency anaemia, pregnancy supplementation.',
    route: 'Oral',
    caution: 'May cause nausea — take with food.',
  }),
  rx({
    id: 'vit-c',
    name: 'Vitamin C 500mg tablets',
    group: 'vitamins',
    category: 'Vitamins & supplements',
    unit: 'strip',
    ingredient: 'Ascorbic acid',
    uses: 'Supplementation, scurvy prevention, support during illness.',
    route: 'Oral',
  }),
  rx({
    id: 'multivit',
    name: 'Multivitamin tablets',
    group: 'vitamins',
    category: 'Vitamins & supplements',
    unit: 'strip',
    ingredient: 'Multivitamins',
    uses: 'Vitamin deficiency, general wellness support.',
    route: 'Oral',
  }),
  rx({
    id: 'vit-d',
    name: 'Vitamin D3 capsules',
    group: 'vitamins',
    category: 'Vitamins & supplements',
    unit: 'strip',
    ingredient: 'Cholecalciferol',
    uses: 'Bone health, vitamin D deficiency.',
    route: 'Oral',
  }),

  rx({
    id: 'microgynon',
    name: 'Microgynon (combined OCP)',
    group: 'family_planning',
    category: 'Family planning & contraceptives',
    unit: 'strip',
    ingredient: 'Ethinylestradiol + levonorgestrel',
    uses: 'Contraception — daily oral pill.',
    route: 'Oral',
    caution: 'Prescription / counseling. Avoid if pregnant or history of thrombosis.',
  }),
  rx({
    id: 'depo',
    name: 'Medroxyprogesterone injection (Depo)',
    group: 'family_planning',
    category: 'Family planning & contraceptives',
    unit: 'vial',
    ingredient: 'Medroxyprogesterone',
    uses: 'Contraception — injection every 3 months.',
    route: 'Injection IM',
    caution: 'Prescription. Administer by trained staff only.',
  }),
  rx({
    id: 'condoms',
    name: 'Male condoms',
    group: 'family_planning',
    category: 'Family planning & contraceptives',
    unit: 'pcs',
    ingredient: '—',
    uses: 'Contraception and STI barrier protection.',
    route: 'Barrier',
  }),
  rx({
    id: 'levonorgestrel',
    name: 'Levonorgestrel 1.5mg (emergency)',
    group: 'family_planning',
    category: 'Family planning & contraceptives',
    unit: 'strip',
    ingredient: 'Levonorgestrel',
    uses: 'Emergency contraception after unprotected sex (within 72h).',
    route: 'Oral — single dose',
    caution: 'Not for routine contraception.',
  }),

  rx({
    id: 'clotrimazole-cream',
    name: 'Clotrimazole 1% cream',
    group: 'topical',
    category: 'Creams, ointments & gels',
    unit: 'tube',
    ingredient: 'Clotrimazole',
    uses: 'Thrush, athlete\'s foot, fungal skin infections.',
    route: 'Topical — apply to skin',
  }),
  rx({
    id: 'gentamicin-cream',
    name: 'Gentamicin cream',
    group: 'topical',
    category: 'Creams, ointments & gels',
    unit: 'tube',
    ingredient: 'Gentamicin',
    uses: 'Superficial skin bacterial infections (as directed).',
    route: 'Topical',
    caution: 'Prescription. Do not use long-term without advice.',
  }),
  rx({
    id: 'hydrocortisone-cream',
    name: 'Hydrocortisone 1% cream',
    group: 'topical',
    category: 'Creams, ointments & gels',
    unit: 'tube',
    ingredient: 'Hydrocortisone',
    uses: 'Skin allergy, mild eczema, inflammation.',
    route: 'Topical — avoid prolonged use on face',
    caution: 'Steroid — do not use on viral/fungal rashes without advice.',
  }),
  rx({
    id: 'betamethasone-cream',
    name: 'Betamethasone cream',
    group: 'topical',
    category: 'Creams, ointments & gels',
    unit: 'tube',
    ingredient: 'Betamethasone',
    uses: 'Eczema, psoriasis, severe skin inflammation.',
    route: 'Topical',
    caution: 'Prescription steroid — short courses only.',
  }),

  rx({
    id: 'cotrim-susp',
    name: 'Co-trimoxazole 240mg/5ml suspension',
    group: 'paediatric',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Co-trimoxazole',
    uses: 'Infections in children (pneumonia, UTI per STG).',
    route: 'Oral',
    caution: 'Prescription. Dose by weight.',
  }),
  rx({
    id: 'iron-syrup',
    name: 'Ferrous sulfate paediatric syrup',
    group: 'paediatric',
    category: 'Syrups & suspensions',
    unit: 'bottle',
    ingredient: 'Iron',
    uses: 'Anaemia in children.',
    route: 'Oral',
    caution: 'Keep out of reach of children — overdose is dangerous.',
  }),

  rx({
    id: 'benz-penicillin',
    name: 'Benzathine penicillin 2.4 MU injection',
    group: 'injections',
    category: 'Injections & vaccines',
    unit: 'vial',
    ingredient: 'Benzathine penicillin',
    uses: 'Syphilis, rheumatic fever prophylaxis, primary care IM (program).',
    route: 'Injection IM — deep',
    caution: 'Prescription. Check allergy; trained provider only.',
  }),
  rx({
    id: 'gentamicin-inj',
    name: 'Gentamicin injection',
    group: 'injections',
    category: 'Injections & vaccines',
    unit: 'vial',
    ingredient: 'Gentamicin',
    uses: 'Severe hospital infections.',
    route: 'Injection IV/IM',
    caution: 'Prescription. Hospital use preferred.',
  }),
  rx({
    id: 'tt-vaccine',
    name: 'Tetanus toxoid vaccine',
    group: 'injections',
    category: 'Injections & vaccines',
    unit: 'vial',
    ingredient: 'Tetanus toxoid',
    uses: 'Tetanus immunization — national schedule.',
    route: 'Injection IM',
    caution: 'Keep cold chain; follow immunization schedule.',
  }),

  rx({
    id: 'bp-cuff',
    name: 'Blood pressure cuff',
    group: 'devices',
    category: 'Medical devices (BP, thermometer)',
    unit: 'pcs',
    ingredient: '—',
    uses: 'Blood pressure monitoring at shop or home.',
    route: 'Device',
  }),
  rx({
    id: 'thermometer',
    name: 'Digital thermometer',
    group: 'devices',
    category: 'Medical devices (BP, thermometer)',
    unit: 'pcs',
    ingredient: '—',
    uses: 'Body temperature measurement (fever).',
    route: 'Device',
  }),
  rx({
    id: 'gauze',
    name: 'Gauze bandage roll',
    group: 'devices',
    category: 'First aid & wound care',
    unit: 'pcs',
    ingredient: '—',
    uses: 'Wound dressing and cleaning.',
    route: 'External use',
  }),
  rx({
    id: 'plaster',
    name: 'Adhesive plaster strips',
    group: 'devices',
    category: 'First aid & wound care',
    unit: 'box',
    ingredient: '—',
    uses: 'Small cuts and grazes.',
    route: 'External use',
  }),
];

const drugById = new Map(PHARMACY_COMMON_DRUGS.map((d) => [d.id, d]));
const groupLabelById = new Map(PHARMACY_THERAPEUTIC_GROUPS.map((g) => [g.id, g.label]));

/** @param {string} id */
export function getPharmacyCommonDrug(id) {
  return drugById.get(id) || null;
}

/** @param {PharmacyCommonDrug} drug */
export function pharmacyDrugIsPrescription(drug) {
  return String(drug?.category || '').includes('(Rx)');
}

/** @param {PharmacyCommonDrug} drug */
export function pharmacyDrugGroupLabel(drug) {
  return groupLabelById.get(drug?.group) || drug?.group || '';
}

/** @param {string} groupId '' = all */
export function filterPharmacyCommonDrugs(groupId, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  return PHARMACY_COMMON_DRUGS.filter((d) => {
    if (groupId && d.group !== groupId) return false;
    if (!q) return true;
    const hay = `${d.name} ${d.ingredient} ${d.uses} ${d.route} ${d.caution || ''} ${d.category}`.toLowerCase();
    return hay.includes(q);
  });
}

/** @param {PharmacyCommonDrug} drug */
export function pharmacyDrugFormDefaults(drug) {
  return {
    name: drug.name,
    category: drug.category,
    unit: drug.unit,
  };
}
